import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CustomTable from "../../../components/table/table";
import { PROJECT_MANAGEMENT } from "../../../constants/route-paths";
import { numberFormat, toUSD } from "../../../util/currency-formatter";
import { PerPageCount } from "../../../util/enums";
import moment from "moment";
import { getRetentionList, getTenantRetentionTotal } from "src/actions/invoice.action";

function RetentionList(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [retentionData, setRetentionData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(PerPageCount.DEFAULT);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');

    useEffect(() => {
        loadRetentionList();
    }, [currentPage, itemsPerPage, search]);

    const loadRetentionList = async () => {
        setIsLoading(true);
        try {
            const { list, totalCount } = await dispatch(getRetentionList({
                page: currentPage,
                limit: itemsPerPage,
                search,
            }));
            const { tenantRetentionTotal } = await dispatch(getTenantRetentionTotal({}));
            // To show OverAll retention in the last row
            list.push({
                isSummary: true,
                name: "Total in Retention",
                retentionAmount: tenantRetentionTotal
            })
            setRetentionData(list);
            setTotalCount(totalCount);
        } catch (error) {
            console.error("Error loading retention list:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageHandler = (page) => { setCurrentPage(page); };

    const limitChangeHandler = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    const searchHandler = (value) => {
        if (value !== search) {
            setSearch(value);
            setCurrentPage(1);
        }
    };

    const columns = [
        {
            title: "Project Name",
            field: "name",
            dataCy: "project-name",
            render: (rowData) => {
                if (rowData?.isSummary) {
                    return (
                        <span className="fw-bold text-dark fs-6">{rowData.name}</span>
                    )
                }
                return (
                    <Link className="cursor-pointer" to={`/${PROJECT_MANAGEMENT}/invoices/${rowData.id}`}>
                        {rowData.name}
                    </Link>
                )
            },
        },
        {
            title: "Contract Amount",
            field: "contractAmount",
            class: "text-right",
            dataCy: "contract-amount",
            render: (rowData) => (
                <span className="text-right">
                    {!rowData?.isSummary ? numberFormat(rowData.contractAmount) : ""}
                </span>
            ),
        },
        {
            title: "Claimed Amount",
            field: "claimAmount",
            class: "text-right",
            dataCy: "claimed-amount",
            render: (rowData) => (
                <span className="text-right">
                    {!rowData?.isSummary ? numberFormat(rowData.claimAmount) : ""}
                </span>
            )
        },
        {
            title: "Retention Held",
            field: "retentionAmount",
            class: "text-right",
            dataCy: "retention-held",
            render: (rowData) => (
                <span className={`${rowData?.isSummary ? "fw-bold text-dark fs-6" : ""} text-right`}>
                    {numberFormat(rowData.retentionAmount)}
                </span>
            ),
        },
        {
            title: "DLP Period",
            field: "dlp_months",
            dataCy: "dlp-period",
            render: (rowData) => !rowData?.isSummary ? rowData.dlp_months || "-" : null,
        },
        {
            title: "Completion Date",
            field: "completionDate",
            dataCy: "completion-date",
            render: (rowData) => !rowData?.isSummary
                ? rowData.completionDate
                    ? moment(rowData.completionDate).format("DD/MM/YYYY")
                    : "-"
                : null,
        },
        {
            title: "Retention Release Date",
            field: "retentionReleaseDate",
            dataCy: "retention-release-date",
            render: (rowData) => !rowData?.isSummary
                ? rowData.retentionReleaseDate
                    ? moment(rowData.retentionReleaseDate).format("DD/MM/YYYY")
                    : "-"
                : null,
        },
    ];

    return (
        <div className="retention-list-container">
            <h2 className="content-heading text-uppercase">Retention List</h2>
            <CustomTable
                columns={columns}
                data={retentionData}
                isLoading={isLoading}
                paginationCount={totalCount}
                currentPage={currentPage}
                pageHandler={pageHandler}
                limitChangeHandler={limitChangeHandler}
                itemsPerPage={itemsPerPage}
                searchHandler={searchHandler}
                externalSearchValue={search}
            />
        </div >
    );
}

export default RetentionList;